import React, { useState } from "react";
import { Briefcase, MapPin, Building2, ChevronDown, ChevronUp, Check, Clock } from "lucide-react";
import { useRedirect } from "../../../Shared/Hooks/useRedirect ";
import { OpportunityIntelligence } from "../Types/CandidateIntelligenceTypes";
import { Section, EmptyState, formatShortDate } from "./Section";

interface OpportunitiesSectionProps {
  opportunities: OpportunityIntelligence;
}

const statusColor = (status: string | null): { label: string; cls: string } => {
  const s = status?.toUpperCase();
  if (s === "ACCEPTED") return { label: "Active", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" };
  if (s === "REJECTED") return { label: "Declined", cls: "bg-rose-500/15 text-rose-300 border-rose-500/30" };
  if (s === "EXPIRED") return { label: "Expired", cls: "bg-slate-700/60 text-slate-400 border-slate-600" };
  return { label: "Invited", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" };
};

export const OpportunitiesSection: React.FC<OpportunitiesSectionProps> = ({ opportunities }) => {
  const redirect = useRedirect();
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? opportunities.opportunities : opportunities.opportunities.slice(0, 3);

  return (
    <Section
      icon={<Briefcase size={17} />}
      title="Opportunity Intelligence"
      subtitle="Roles matched to your profile, with reasons"
      right={
        <span className="rounded-full bg-emerald-500/15 px-2.5 py-1 text-[11px] font-bold text-emerald-300">
          {opportunities.totalMatches} matches
        </span>
      }
    >
      {opportunities.opportunities.length === 0 ? (
        <EmptyState label="No matching roles right now — complete profile improvements to surface more" />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {visible.map((op) => {
            const st = statusColor(op.invitationStatus);
            return (
              <article
                key={op.jobId}
                className="flex flex-col rounded-2xl border border-slate-800 bg-slate-900/60 p-4 transition hover:border-cyan-500/40"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-bold text-slate-100">{op.jobTittle ?? "Untitled role"}</h3>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400">
                      <Building2 size={12} />
                      {op.companyName ?? "—"}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-xl bg-gradient-to-br from-cyan-500/20 to-indigo-500/20 px-2.5 py-1.5 text-lg font-black tabular-nums text-cyan-300">
                    {op.matchPercent}
                    <span className="text-[10px]">%</span>
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5 text-[11px] text-slate-300">
                  {op.roleLevel ? (
                    <span className="rounded-full border border-slate-700 bg-slate-800/70 px-2 py-0.5">{op.roleLevel}</span>
                  ) : null}
                  {op.miniExperience || op.maxExperience ? (
                    <span className="rounded-full border border-slate-700 bg-slate-800/70 px-2 py-0.5">
                      {op.miniExperience || "0"}–{op.maxExperience || "∞"} yrs
                    </span>
                  ) : null}
                  {op.workType ? (
                    <span className="rounded-full border border-slate-700 bg-slate-800/70 px-2 py-0.5">{op.workType}</span>
                  ) : null}
                </div>

                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400">
                  <span className="flex items-center gap-1">
                    <MapPin size={11} />
                    {op.location?.length ? op.location.join(", ") : "Any"}
                  </span>
                  {op.noticePeriodDays ? <span>Notice: {op.noticePeriodDays}d</span> : null}
                </div>

                <div className="mt-3 flex-1">
                  <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">Why this matches</p>
                  <ul className="flex flex-col gap-1">
                    {(op.matchReasons.slice(0, 3)).map((reason, idx) => (
                      <li key={idx} className="flex items-start gap-1.5 text-xs text-slate-300">
                        <Check size={12} className="mt-0.5 shrink-0 text-emerald-400" />
                        {reason}
                      </li>
                    ))}
                  </ul>
                </div>

                <footer className="mt-3 flex items-center justify-between border-t border-slate-800 pt-2.5">
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${st.cls}`}>
                    {op.invitationStatus ? (
                      <span className="flex items-center gap-1">
                        {op.invitationStatus === "PENDING" ? <Clock size={11} /> : <Check size={11} />}
                        {st.label}
                      </span>
                    ) : (
                      "Open role"
                    )}
                  </span>
                  {op.invitedAt ? (
                    <span className="text-[11px] text-slate-500">{formatShortDate(op.invitedAt)}</span>
                  ) : null}
                </footer>
              </article>
            );
          })}
        </div>
      )}

      {opportunities.opportunities.length > 3 ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl border border-slate-800 bg-slate-900/40 py-2 text-xs font-semibold text-slate-400 transition hover:text-cyan-300"
        >
          {expanded ? (
            <>
              <ChevronUp size={14} /> Show fewer
            </>
          ) : (
            <>
              <ChevronDown size={14} /> Show all {opportunities.totalMatches} matching roles
            </>
          )}
        </button>
      ) : null}

      {opportunities.totalMatches > 0 ? (
        <p className="mt-2 text-center text-[11px] text-slate-500">
          Tip: accept an invitation to mark a role as an active opportunity
          <button type="button" onClick={() => redirect("/candidate/dashboard")} className="ml-1 text-cyan-400 hover:underline">
            view invitations
          </button>
        </p>
      ) : null}
    </Section>
  );
};